import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-a788eb58"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "spelling-word-detail" }
const _hoisted_2 = { class: "detail-wrapper" }
const _hoisted_3 = { class: "mb-4" }
const _hoisted_4 = { class: "mobile-back" }
const _hoisted_5 = ["src"]
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "back-text" }, "Back", -1))
const _hoisted_7 = { class: "page-title-wrapper" }
const _hoisted_8 = { class: "work-sans" }
const _hoisted_9 = { class: "student-name-heading" }
const _hoisted_10 = {
  key: 0,
  class: "loading-state"
}
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "Loading word data...", -1))
const _hoisted_12 = {
  key: 1,
  class: "detail-content"
}
const _hoisted_13 = {
  key: 0,
  class: "summary-loader"
}
const _hoisted_14 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "summary-loader__text" }, "Loading word data...", -1))
const _hoisted_15 = { class: "summary-head" }
const _hoisted_16 = { class: "summary-title" }
const _hoisted_17 = { class: "eyebrow" }
const _hoisted_18 = { class: "level-info" }
const _hoisted_19 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "level-separator" }, "|", -1))
const _hoisted_20 = { class: "level-status" }
const _hoisted_21 = { class: "etymology-section" }
const _hoisted_22 = ["innerHTML"]
const _hoisted_23 = { class: "exposure-table" }
const _hoisted_24 = ["onClick"]
const _hoisted_25 = { class: "flex items-center gap-2" }
const _hoisted_26 = { class: "status-text" }
const _hoisted_27 = {
  key: 2,
  class: "empty-state"
}
const _hoisted_28 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Couldn't load this word", -1))
const _hoisted_29 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "Something went wrong while loading this word's data. Please check your connection and try again.", -1))
const _hoisted_30 = {
  key: 3,
  class: "empty-state"
}
const _hoisted_31 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Word not found", -1))
const _hoisted_32 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "We could not locate that vocabulary word. Please return to the list and choose another word.", -1))
const _hoisted_33 = [
  _hoisted_31,
  _hoisted_32
]
const _hoisted_34 = ["innerHTML"]

import { computed, ref, onMounted, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useLingoStore } from "@/stores/lingoStore";
import { useLearnerStore } from "@/stores/learnerStore";
import i18n from "@/plugins/i18n";
import type { KnowledgeComponent, ExposureHistory } from "@/interfaces/lingo.interfaces";
import LingoDataTable from "@/modules/main/components/manage/LingoDataTableComponent.vue";
import LingoModal from "@/modules/main/components/manage/LingoModalComponent.vue";
import LingoTooltipIcon from "@/modules/main/components/manage/LingoTooltipIconComponent.vue";
import AnswerReviewModal from "@/components/manage/AnswerReviewModal.vue";
import BlockedPracticeFullExplanationModal from "@/components/manage/BlockedPracticeFullExplanationModal.vue";
import getKcStatusTooltip from "@/modules/main/utils/kc";
import { logger } from "@/utils/logger";
import { WORD_NOT_FOUND_EVENT, buildWordNotFoundLog } from "@/utils/lingo-word-not-found";
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?component';
import BuildingIcon from '@/assets/icons/building-05.svg?component';
import SpinnerLoader from "@/components/global/SpinnerLoader.vue";
import api from "@/api";


export default /*#__PURE__*/_defineComponent({
  __name: 'LingoStudentVocabularyKC',
  setup(__props) {

const isArrowIconString = typeof ArrowLeftIcon === "string";

const isLoading = ref(false);

// Route and store
const route = useRoute();
const router = useRouter();
const lingoStore = useLingoStore();
const learnerStore = useLearnerStore();

// Grab route params (path: /main/lingo-vocabulary-word/:studentId/:kcId)
const studentId = computed(() => String(route.params.studentId ?? ""));
const kcIdParam = computed(() => String(route.params.kcId ?? ""));
const normalizedKcId = computed(() => {
  const raw = kcIdParam.value;
  if (!raw) return null;
  const decoded = decodeURIComponent(String(raw));
  const asNumber = Number(decoded);
  if (Number.isFinite(asNumber) && String(asNumber) === decoded) return asNumber;
  return decoded;
});

// Get the student Lingo data from the store
const studentLingoData = computed(() => lingoStore.getLingoDataByStudentId(studentId.value) || null);

//breadcrumbs
const pages = computed(() => {
  const base = [{ name: "Lingo Data", path: "/main/lingo-data" }];
  base.push({
    name: "Data for " + displayName.value,
    path: "/main/lingo-data/student/" + studentId.value
  });
  base.push({
    name: `Know the word: ${kc.value?.word || ""}`,
    path: "#"
  });
  return base;
});


const searchableKcs = computed(() => {
  const allKcs = Array.isArray(studentLingoData.value?.allKCs) ? studentLingoData.value?.allKCs : [];
  const currentKcs = Array.isArray(studentLingoData.value?.currentVocabularyActivityKcs)
    ? studentLingoData.value?.currentVocabularyActivityKcs
    : [];

  const merged = new Map<string, any>();
  const buildKey = (kc: any) => {
    if (!kc || typeof kc !== "object") return null;
    const type = String(kc.type || "vocabulary");
    const idPart =
      kc.strapiWordId != null
        ? `strapi:${String(kc.strapiWordId)}`
        : kc._id != null
          ? `mongo:${String(kc._id)}`
          : kc.id != null
            ? `id:${String(kc.id)}`
            : kc.word != null
              ? `word:${String(kc.word).toLowerCase()}`
              : null;
    return idPart ? `${type}:${idPart}` : null;
  };
  const getHistorySize = (kc: any) =>
    Array.isArray(kc?.exposureHistory) ? kc.exposureHistory.length : 0;

  const upsert = (kc: any) => {
    const key = buildKey(kc);
    if (!key) return;
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, kc);
      return;
    }
    if (getHistorySize(kc) > getHistorySize(existing)) {
      merged.set(key, kc);
    }
  };

  allKcs.forEach(upsert);
  currentKcs.forEach(upsert);

  return [...merged.values()].filter((kc: any) => kc && kc.type === "vocabulary");
});

// Resolve the KC for this student by matching word, strapiWordId, _id or id
const kc = computed<KnowledgeComponent | null>(() => {
  if (normalizedKcId.value == null) return null;
  const searchId = String(normalizedKcId.value);
  const all = searchableKcs.value || [];

  let found = all.find((k: any) => k?.strapiWordId != null && String(k.strapiWordId) === searchId);

  if (!found) {
    found = all.find((k: any) => k?.word != null && String(k.word) === searchId);
  }

  if (!found) {
    found = all.find((k: any) => String(k.id ?? "") === searchId || String(k._id ?? "") === searchId);
  }

  if (!found) {
    const lower = searchId.toLowerCase();
    found = all.find((k: any) => typeof k?.word === "string" && k.word.toLowerCase() === lower);
  }

  return found || null;
});

// Simple mapping for status label used in template
const STATUS_LABELS: Record<string, string> = {
  not_yet_seen: "Not yet seen",
  seen: "Seen",
  learning: "Learning",
  practicing: "Reinforcing",
  mastered_prior_learning: "Mastered (Prior Learning)",
  mastered_new_learning: "Mastered (Gap Filled)",
};

const statusBadge = computed(() => {
  if (!kc.value) return { label: "Not available", color: "gray", description: "" };
  const label = STATUS_LABELS[kc.value.status] || STATUS_LABELS.not_yet_seen;
  return { label, color: "gray", description: "" };
});

// Tooltip text / visible description for current KC status
const statusDescriptionKey = computed(() => {
  return getKcStatusTooltip(kc.value?.status);
});

const statusDescription = computed(() => {
  const key = statusDescriptionKey.value;
  if (!key) return "";
  return i18n.global.t(key).toString();
});

// Format exposure history rows for the table
function formatTimestamp(ts: string | Date | undefined | null) {
  if (!ts) return "—";
  const d = new Date(ts as any);
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    }).format(d);
  } catch {
    return "—";
  }
}

function mapStatusLabel(raw: string | null | undefined) {
  if (!raw) return null;
  const key = String(raw).trim();
  if (!key) return null;
  if (STATUS_LABELS[key as keyof typeof STATUS_LABELS]) {
    return STATUS_LABELS[key as keyof typeof STATUS_LABELS];
  }
  if (/^true$/i.test(key) || /^correct$/i.test(key)) return "Correct";
  if (/^false$/i.test(key) || /^incorrect$/i.test(key)) return "Incorrect";
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function parseOptionalDate(value: any): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function normalizeToken(value: any): string {
  if (value === null || value === undefined) return "";
  return String(value).trim().toLowerCase();
}

function isBlockedTaggedEntry(entry: any): boolean {
  if (!entry || typeof entry !== "object") {
    return false;
  }
  return Boolean(
    normalizeToken(entry.activityMode) === "blocked" ||
    (typeof entry.blockedSessionKey === "string" && entry.blockedSessionKey.trim().length) ||
    (typeof entry.blockedAggregateLabel === "string" && entry.blockedAggregateLabel.trim().length) ||
    parseOptionalDate(entry.blockedCompletedAt)
  );
}

function sortBlockedSessionEntries(entries: ExposureHistory[]): ExposureHistory[] {
  return [...(Array.isArray(entries) ? entries : [])].sort((left: any, right: any) => {
    const leftIndex = Number(left?.blockedQuestionIndex);
    const rightIndex = Number(right?.blockedQuestionIndex);
    const leftHasIndex = Number.isFinite(leftIndex);
    const rightHasIndex = Number.isFinite(rightIndex);

    if (leftHasIndex && rightHasIndex && leftIndex !== rightIndex) {
      return leftIndex - rightIndex;
    }
    if (leftHasIndex && !rightHasIndex) return -1;
    if (!leftHasIndex && rightHasIndex) return 1;

    const leftTs = parseOptionalDate(left?.timestamp);
    const rightTs = parseOptionalDate(right?.timestamp);
    if (!leftTs && !rightTs) return 0;
    if (!leftTs) return -1;
    if (!rightTs) return 1;
    return leftTs.getTime() - rightTs.getTime();
  });
}

function isBlockedAggregateCompleteEntry(entry: any): boolean {
  if (!entry || typeof entry !== "object") return false;
  if (parseOptionalDate(entry.blockedCompletedAt)) {
    return true;
  }

  const blockedMode = normalizeToken(entry.activityMode) === "blocked";
  if (!blockedMode) {
    return false;
  }

  const hasAggregateLabel =
    typeof entry.blockedAggregateLabel === "string" &&
    entry.blockedAggregateLabel.trim().length > 0;
  const hasFinalStatus =
    typeof entry.blockedFinalStatus === "string" &&
    entry.blockedFinalStatus.trim().length > 0;

  return hasAggregateLabel || hasFinalStatus;
}

function getBlockedAggregateSessionKey(entry: any, fallbackRow: any = {}): string | null {
  if (!entry || typeof entry !== "object") return null;
  if (typeof entry.blockedSessionKey === "string" && entry.blockedSessionKey.trim().length) {
    return entry.blockedSessionKey.trim();
  }
  const completedAt = parseOptionalDate(entry.blockedCompletedAt);
  const primaryWord = entry.word || fallbackRow.word || kc.value?.word || "unknown-word";
  if (completedAt) {
    return `blocked:${primaryWord}:${completedAt.toISOString()}`;
  }

  const blockedMode = normalizeToken(entry.activityMode) === "blocked";
  if (!blockedMode) {
    return null;
  }

  const blockedLabel =
    typeof entry.blockedAggregateLabel === "string"
      ? entry.blockedAggregateLabel.trim().toLowerCase()
      : "";
  if (!blockedLabel) {
    return null;
  }

  const timestamp = parseOptionalDate(entry.timestamp);
  const daySegment = timestamp ? timestamp.toISOString().slice(0, 10) : "unknown-day";
  return `blocked:${primaryWord}:${blockedLabel}:${daySegment}`;
}

function getExposureQuestionLabel(entry: any, fallbackRow: any = {}): string {
  const blockedLabel = typeof entry?.blockedAggregateLabel === "string" ? entry.blockedAggregateLabel.trim() : "";
  if (blockedLabel) {
    return blockedLabel;
  }
  if (entry && typeof entry.isCorrect === "boolean") {
    return entry.isCorrect ? "Correct" : "Incorrect";
  }
  if (typeof fallbackRow?.isCorrect === "boolean") {
    return fallbackRow.isCorrect ? "Correct" : "Incorrect";
  }
  return "—";
}

function getExposureStatusLabel(entry: any): string {
  const defaultLabel = "Seen";
  if (!entry || typeof entry !== "object") return defaultLabel;

  const statusCandidates = [
    entry.blockedFinalStatus,
    entry.kcStatusAfter,
    entry.kcStatus,
    entry.statusAfter,
    entry.status,
    entry.resultStatus,
    entry.statusLabel,
    entry.progressStatus,
    entry.result && entry.result.status
  ];

  for (const candidate of statusCandidates) {
    const label = mapStatusLabel(candidate);
    if (label) return label;
  }

  return defaultLabel;
}

function getExposureStatusSource(entry: any): string {
  if (!entry || typeof entry !== "object") return "seen";
  const statusCandidates = [
    entry.blockedFinalStatus,
    entry.kcStatusAfter,
    entry.kcStatus,
    entry.statusAfter,
    entry.status,
    entry.resultStatus,
    entry.statusLabel,
    entry.progressStatus,
    entry.result && entry.result.status
  ];
  for (const candidate of statusCandidates) {
    if (typeof candidate === "string" && candidate.trim().length) {
      return candidate;
    }
  }
  return "seen";
}

const exposureRows = computed(() => {
  if (!kc.value || !Array.isArray(kc.value.exposureHistory)) return [];
  return (kc.value.exposureHistory as ExposureHistory[])
    .slice()
    .reverse()
    .map((entry, idx) => {
      const date = formatTimestamp(entry.timestamp as any);
      return {
        rowKey: `${date}_${idx}`,
        isCorrect: Boolean(entry.isCorrect),
        displayedAt: date,
        rawEntry: entry
      };
    });
});

function buildDefaultExposureSummaryRow(row: any) {
  const entry = row.rawEntry || {};
  const statusLabel = getExposureStatusLabel(entry);
  return {
    rowKey: row.rowKey,
    date: row.displayedAt,
    question: getExposureQuestionLabel(entry, row),
    status: statusLabel,
    statusSource: getExposureStatusSource(entry),
    exposure: entry
  };
}

function buildBlockedAggregateSummaryRow(groupRows: any[], blockedSessionKey: string) {
  const safeRows = Array.isArray(groupRows) ? groupRows : [];
  const representativeRow = safeRows[0] || null;
  const representativeEntry = representativeRow?.rawEntry || {};
  const sessionEntries = safeRows.map((row: any) => row.rawEntry || {}).filter(Boolean);

  const parsedCompletionDates = sessionEntries
    .map((entry: any) => parseOptionalDate(entry.blockedCompletedAt))
    .filter(Boolean) as Date[];
  const completedAt = parsedCompletionDates.length
    ? new Date(Math.max(...parsedCompletionDates.map(date => date.getTime())))
    : parseOptionalDate(representativeEntry.timestamp);

  const totalQuestionsCandidate = sessionEntries
    .map((entry: any) => Number(entry.blockedTotalQuestions))
    .find((value: number) => Number.isFinite(value) && value > 0);
  const totalQuestions = totalQuestionsCandidate || safeRows.length || 10;

  const aggregateLabelCandidate = sessionEntries
    .map((entry: any) => (typeof entry.blockedAggregateLabel === "string" ? entry.blockedAggregateLabel.trim() : ""))
    .find(Boolean);
  const aggregateLabel = aggregateLabelCandidate || `Blocked Practice (x${totalQuestions})`;

  const finalStatusCandidate = sessionEntries
    .map((entry: any) =>
      typeof entry.blockedFinalStatus === "string" && entry.blockedFinalStatus.trim()
        ? entry.blockedFinalStatus.trim()
        : null
    )
    .find(Boolean);

  const aggregateEntry = {
    ...representativeEntry,
    blockedSessionKey,
    blockedTotalQuestions: totalQuestions,
    blockedAggregateLabel: aggregateLabel,
    blockedCompletedAt: completedAt || representativeEntry.blockedCompletedAt || representativeEntry.timestamp || null,
    blockedFinalStatus: finalStatusCandidate || representativeEntry.blockedFinalStatus || representativeEntry.status || null
  };

  const statusLabel = getExposureStatusLabel(aggregateEntry);
  const statusSource = getExposureStatusSource(aggregateEntry);

  return {
    rowKey: `blocked_${blockedSessionKey}`,
    date: completedAt ? formatTimestamp(completedAt) : representativeRow?.displayedAt || "—",
    question: getExposureQuestionLabel(aggregateEntry, representativeRow || {}),
    status: statusLabel,
    statusSource,
    exposure: sessionEntries[0] || null,
    blockedSessionEntries: sessionEntries,
    aggregateEntry
  };
}

function groupCompletedBlockedRows(rows: any[]) {
  return (Array.isArray(rows) ? rows : []).reduce((groups: Record<string, any[]>, row: any) => {
    const entry = row?.rawEntry || {};
    if (!isBlockedAggregateCompleteEntry(entry)) {
      return groups;
    }
    const sessionKey = getBlockedAggregateSessionKey(entry, row);
    if (!sessionKey) {
      return groups;
    }
    if (!groups[sessionKey]) {
      groups[sessionKey] = [];
    }
    groups[sessionKey].push(row);
    return groups;
  }, {});
}

const exposureSummaryRows = computed(() => {
  const rows = exposureRows.value;
  const blockedGroupsBySession = groupCompletedBlockedRows(rows);

  const emittedBlockedSessions = new Set<string>();

  return rows.reduce((summaryRows: any[], row: any) => {
    const entry = row.rawEntry || {};
    const blockedSessionKey = getBlockedAggregateSessionKey(entry, row);
    const blockedGroup = blockedSessionKey ? blockedGroupsBySession[blockedSessionKey] : null;

    if (blockedSessionKey && blockedGroup && blockedGroup.length) {
      if (emittedBlockedSessions.has(blockedSessionKey)) {
        return summaryRows;
      }
      emittedBlockedSessions.add(blockedSessionKey);
      summaryRows.push(buildBlockedAggregateSummaryRow(blockedGroup, blockedSessionKey));
      return summaryRows;
    }

    summaryRows.push(buildDefaultExposureSummaryRow(row));
    return summaryRows;
  }, []);
});

// table column definition for DataTable component
const tableColumns = computed(() => [
  { key: "date", label: "Date" },
  { key: "question", label: "Question(s)" },
  { key: "status", label: "Status" }
]);

const etymologyText = computed(() => {
  if (!kc.value || !kc?.value?.word) return "";
  const wordName = String(kc?.value?.word ?? "").trim();
  const wordData = wordName ? lingoStore.getWordByName(wordName) : null;
  const vocabularyDescription = wordData && (wordData.vocabularyDescription);
  if (!vocabularyDescription || typeof vocabularyDescription !== "string") return "";
  return vocabularyDescription.replace(/\*\*\*(.+?)\*\*\*/g, "<strong>$1</strong>");
});

const fullExplanationFallbackText = computed(() => {
  if (!kc.value || !kc?.value?.word) return "";
  const wordName = String(kc?.value?.word ?? "").trim();
  const wordData = wordName ? lingoStore.getWordByName(wordName) : null;
  const vocabularyDescription = wordData && wordData.vocabularyDescription;
  return typeof vocabularyDescription === "string" ? vocabularyDescription : "";
});

const selectedFullExplanationPersona = computed(() => {
  const name = studentLingoData.value?.selectedTeacherBot?.name;
  if (typeof name === "string" && name.trim().length) {
    return name.trim();
  }
  return "Dot";
});

// Modal state
const showExposureModal = ref(false);
const selectedExposureRow = ref<any | null>(null);
const selectedExposure = ref<ExposureHistory | null>(null);
const selectedBlockedSessionEntries = ref<ExposureHistory[]>([]);
const showFullExplanationModal = ref(false);
const fullExplanationLoading = ref(false);
const fullExplanationContent = ref<{ spelling: string; vocabulary: string } | null>(null);

const fullExplanationByThread = computed(() => {
  if (!fullExplanationContent.value) {
    return "";
  }
  return fullExplanationContent.value.vocabulary || "";
});
// derived helpers for exposure rendering
const isVocabQuestion = computed(() => {
  return !!(selectedExposure.value && ["given_word_choose_definition", "given_definition_choose_word", "misuse"].includes(String(selectedExposure.value.itemCategory)));
});

function getQuestionDataFromExposure(): any | null {
  if (!selectedExposure.value) return null;
  const q = selectedExposure.value.question ?? null;
  // common shapes: question.questionData or questionData at top-level
  const qd = q?.questionData ?? (q && typeof q === "object" ? q : null);
  return qd;
}

// Return translated tooltip text for an exposure row (falls back to KC status)
function getTooltipTextForExposure(row: any): string | null {
  const statusSource = row?.statusSource ?? row?.exposure?.status ?? kc.value?.status;
  if (!statusSource) return null;
  const tooltipKey = getKcStatusTooltip(String(statusSource));
  if (!tooltipKey) return null;
  return i18n.global.t(tooltipKey).toString();
}

const questionData = computed(() => getQuestionDataFromExposure());

const sentenceWithOptions = computed(() => {
  const qd = questionData.value;
  if (!qd || !(qd.definition || qd.sentence)) return "";
  // prefer generatedSentence from the exposure if present
  const generated = qd.definition ?? qd.sentence;
  const baseSource = generated ? String(generated) : String(qd.definition ?? qd.sentence);
  const baseEscaped = escapeHtml(baseSource);

  // normalize raw options array from a few possible shapes
  const rawOpts = Array.isArray(qd.options)
    ? qd.options
    : Array.isArray(qd.choices)
      ? qd.choices
      : [];

  const opts = rawOpts
    .map((o: any) => {
      if (o === null || o === undefined) return "";
      if (typeof o === "object") {
        // prefer common text keys
        return escapeHtml(String(o.text ?? ""));
      }
      return escapeHtml(String(o.text));
    })
    .filter((s: string) => s.length > 0);

  // If we have a generated sentence and a word, wrap the word in a span
  let sentenceHtml = baseEscaped;
  const targetWord = String(selectedExposure.value?.word ?? kc?.value?.word ?? "").trim();
  if (generated && targetWord) {
    const escapedWord = escapeHtml(targetWord);
    const re = new RegExp(`(${escapeRegExp(escapedWord)})`, "i");
    sentenceHtml = baseEscaped.replace(re, `<span class="exposure-word-highlight">$1</span>`);
  }

  if (opts.length) {
    const lis = opts.map((o: string) => `<li>${o}</li>`).join("");
    return `${sentenceHtml}<ul class="options-list" aria-label="Options list">${lis}</ul>`;
  }
  return sentenceHtml;
});

function escapeRegExp(str: string) {
  return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function escapeHtml(str: string) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const sentenceWithGivenAnswer = computed(() => {
  const qd = questionData.value;
  if (!qd || !selectedExposure.value) return "";
  qd.sentence = selectedExposure.value.question.generatedSentence;
  const given = selectedExposure.value.givenAnswer ?? "";
  if (selectedExposure.value.itemCategory === "given_word_choose_definition") {
    return String(given);
  } else if (selectedExposure.value.itemCategory === "given_definition_choose_word") {
    return String(given);
  } else if (selectedExposure.value.itemCategory === "misuse") {
    return String(given);
  } else {
    return String(given);
  }
});

const correctedSentenceWithGivenAnswer = computed(() => {
  const qd = questionData.value;
  if (!qd || !selectedExposure.value) return "";

  // normalize options/choices array
  const rawOpts = Array.isArray(qd.options)
    ? qd.options
    : Array.isArray(qd.choices)
      ? qd.choices
      : Array.isArray(qd.questionChoices)
        ? qd.questionChoices
        : [];

  // find the option marked as correct
  let correctOption: any = null;
  if (Array.isArray(rawOpts) && rawOpts.length) {
    correctOption =
      rawOpts.find((o: any) => o?.isCorrect === true || o?.correct === true || o?.is_correct === true) ||
      rawOpts.find((o: any) => typeof o === "object" && (o.text === selectedExposure.value.word || o.label === selectedExposure.value.word)) ||
      rawOpts[0];
  }

  if (selectedExposure.value.itemCategory === "given_word_choose_definition") {
    return String(correctOption.text);
  } else if (selectedExposure.value.itemCategory === "given_definition_choose_word") {
    return String(correctOption.text);
  } else if (selectedExposure.value.itemCategory === "misuse") {
    return String(correctOption.text);
  } else {
    return String(correctOption.text);
  }

});

const resultClass = computed(() => {
  if (!selectedExposure.value) return "";
  return selectedExposure.value.isCorrect ? "result-correct" : "result-incorrect";
});

function buildVocabularyAnswerReviewPayload(
  exposureEntry: ExposureHistory | null,
  reviewOptions: { timestampOverride?: string | null } = {}
) {
  if (!exposureEntry) return null;

  const entry = exposureEntry;
  const question = entry.question || {};
  const questionData = question.questionData || {};
  const itemCategory = entry.itemCategory || question.itemCategory || "given_word_choose_definition";
  const fallbackWord = entry.word || question.word || questionData.word || (kc.value && kc.value.word) || "";
  const givenAnswer = entry.givenAnswer || "";
  const isCorrect = Boolean(entry.isCorrect);
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

  // Build options array from question data
  const optionList = Array.isArray(questionData.options) ? questionData.options : [];

  // Find selected and correct indices
  let selectedIndex = null;
  let correctIndex = null;
  optionList.forEach((opt, idx) => {
    if (opt && typeof opt === "object") {
      if (opt.text && opt.text.toLowerCase() === givenAnswer.toLowerCase()) selectedIndex = idx;
      if (opt.isCorrect === true || opt.correct === true || opt.is_correct === true) correctIndex = idx;
    } else if (typeof opt === "string") {
      if (opt.toLowerCase() === givenAnswer.toLowerCase()) selectedIndex = idx;
    }
  });

  // Build option entries with MC option labels
  const optionEntries = optionList.map((opt, idx) => {
    let text = typeof opt === "object" ? (opt.text || opt.value || opt.label || String(opt)) : String(opt);
    return {
      id: idx,
      text,
      isCorrect: (typeof opt === "object") ? (opt.isCorrect === true || opt.correct === true || opt.is_correct === true) : false,
      meta: null
    };
  });

  // Build question HTML based on item category
  let questionHtml = "";
  if (itemCategory === "given_word_choose_definition") {
    questionHtml = `Which of these descriptions best matches the word <span class="answer-review__highlight">"${escapeHtml(fallbackWord)}"</span>?`;
  } else if (itemCategory === "given_definition_choose_word") {
    const definition = questionData.definition || "";
    questionHtml = definition
      ? `Which word best fits this definition:<br/><span class="answer-review__highlight">"${escapeHtml(definition)}"</span>`
      : `Select the correct word.`;
  } else if (itemCategory === "misuse") {
    questionHtml = questionData.sentence || questionData.definition || "Review the question below.";
  } else {
    questionHtml = questionData.definition || questionData.sentence || "";
  }

  // Get the letter for student's answer (A, B, C, D)
  const studentAnswerLetter = selectedIndex !== null ? alphabet[selectedIndex] : givenAnswer;
  const correctAnswerLetter = correctIndex !== null ? alphabet[correctIndex] : "";

  return {
    word: fallbackWord,
    timestamp: reviewOptions.timestampOverride || formatTimestamp(entry.timestamp),
    itemCategory,
    type: "vocabulary",
    isCorrect,
    questionText: "",
    questionHtml,
    questionDetails: [],
    options: optionEntries,
    selectedIndex,
    correctIndex,
    studentAnswer: studentAnswerLetter,
    correctAnswer: correctAnswerLetter
  };
}

const activeAnswerReview = computed(() => {
  if (selectedBlockedSessionEntries.value.length) {
    const summaryRow = selectedExposureRow.value || {};
    const aggregateEntry = summaryRow.aggregateEntry || {};
    const orderedEntries = sortBlockedSessionEntries(selectedBlockedSessionEntries.value);

    const attempts = orderedEntries
      .map((entry: any, index: number) => {
        const entryTimestamp = formatTimestamp(entry?.timestamp || aggregateEntry.blockedCompletedAt || summaryRow?.date);
        const payload = buildVocabularyAnswerReviewPayload(entry, { timestampOverride: entryTimestamp });
        if (!payload) return null;
        return {
          ...payload,
          attemptNumber: index + 1,
          isCorrect: Boolean(entry?.isCorrect)
        };
      })
      .filter(Boolean);

    const totalQuestionsRaw = Number(aggregateEntry.blockedTotalQuestions);
    const totalQuestions =
      Number.isFinite(totalQuestionsRaw) && totalQuestionsRaw > 0
        ? totalQuestionsRaw
        : attempts.length || 10;

    return {
      word: aggregateEntry.word || selectedExposure.value?.word || kc.value?.word || "",
      timestamp: summaryRow?.date || formatTimestamp(aggregateEntry.blockedCompletedAt || aggregateEntry.timestamp),
      itemCategory: "blocked_practice",
      type: "vocabulary",
      isCorrect: false,
      questionText: "",
      questionHtml: "",
      questionDetails: [],
      isBlockedAggregate: true,
      blockedAttempts: attempts,
      blockedTotalQuestions: totalQuestions,
      blockedAggregateLabel: aggregateEntry.blockedAggregateLabel || `Blocked Practice (x${totalQuestions})`,
      blockedFinalStatus: aggregateEntry.blockedFinalStatus || aggregateEntry.status || null,
      fullExplanationFallback: fullExplanationFallbackText.value
    };
  }

  const payload = buildVocabularyAnswerReviewPayload(selectedExposure.value);
  if (!payload) {
    return null;
  }

  return {
    ...payload,
    fullExplanationFallback: fullExplanationFallbackText.value
  };
});

function collectBlockedSessionEntriesFromSummaryRow(summaryRow: any): ExposureHistory[] {
  const directEntries = Array.isArray(summaryRow?.blockedSessionEntries)
    ? summaryRow.blockedSessionEntries.filter((entry: any) => entry && typeof entry === "object")
    : [];
  const sortedDirectEntries = sortBlockedSessionEntries(directEntries as ExposureHistory[]);

  const aggregateEntry =
    summaryRow?.aggregateEntry ||
    summaryRow?.rawEntry ||
    summaryRow?.exposure ||
    summaryRow?.original?.rawEntry ||
    null;
  if (!aggregateEntry || typeof aggregateEntry !== "object") {
    return sortedDirectEntries;
  }

  const rowLabel = typeof summaryRow?.question === "string" ? summaryRow.question.trim().toLowerCase() : "";
  const rowLooksBlocked = rowLabel.includes("blocked practice") || isBlockedTaggedEntry(aggregateEntry);

  const allEntries = exposureRows.value
    .map((row: any) => row?.rawEntry || null)
    .filter((entry: any) => entry && typeof entry === "object");
  if (!allEntries.length) {
    return sortedDirectEntries;
  }

  const directTotalQuestions = Number(
    summaryRow?.aggregateEntry?.blockedTotalQuestions ||
    summaryRow?.blockedTotalQuestions ||
    sortedDirectEntries[0]?.blockedTotalQuestions
  );
  const expectedTotalQuestions = Number.isFinite(directTotalQuestions) && directTotalQuestions > 0
    ? directTotalQuestions
    : 10;

  if (sortedDirectEntries.length >= expectedTotalQuestions) {
    return sortedDirectEntries;
  }

  const sessionKey = getBlockedAggregateSessionKey(aggregateEntry, summaryRow || {});
  if (sessionKey) {
    const keyedEntries = sortBlockedSessionEntries(allEntries.filter((entry: any) => {
      const entrySessionKey = getBlockedAggregateSessionKey(entry, summaryRow || {});
      return entrySessionKey === sessionKey;
    }) as ExposureHistory[]);
    if (keyedEntries.length) {
      return keyedEntries;
    }
  }

  const completionDate = parseOptionalDate(aggregateEntry.blockedCompletedAt);
  if (completionDate) {
    const completedAtMs = completionDate.getTime();
    const sameCompletionEntries = sortBlockedSessionEntries(allEntries.filter((entry: any) => {
      const entryCompletionDate = parseOptionalDate(entry?.blockedCompletedAt);
      return entryCompletionDate && entryCompletionDate.getTime() === completedAtMs;
    }) as ExposureHistory[]);
    if (sameCompletionEntries.length) {
      return sameCompletionEntries;
    }
  }

  const aggregateLabel =
    typeof aggregateEntry.blockedAggregateLabel === "string"
      ? aggregateEntry.blockedAggregateLabel.trim()
      : "";
  if (aggregateLabel) {
    const labelEntries = sortBlockedSessionEntries(allEntries.filter((entry: any) => {
      return (
        typeof entry?.blockedAggregateLabel === "string" &&
        entry.blockedAggregateLabel.trim() === aggregateLabel
      );
    }) as ExposureHistory[]);
    if (labelEntries.length) {
      return labelEntries;
    }
  }

  const wordToken = normalizeToken(aggregateEntry.word || summaryRow?.word || kc.value?.word);
  const blockedModeEntries = allEntries
    .filter((entry: any) => (rowLooksBlocked ? isBlockedTaggedEntry(entry) : normalizeToken(entry.activityMode) === "blocked"))
    .filter((entry: any) => !wordToken || normalizeToken(entry.word) === wordToken)
    .sort((left: any, right: any) => {
      const leftTs = parseOptionalDate(left?.timestamp);
      const rightTs = parseOptionalDate(right?.timestamp);
      if (!leftTs && !rightTs) return 0;
      if (!leftTs) return -1;
      if (!rightTs) return 1;
      return leftTs.getTime() - rightTs.getTime();
    }) as ExposureHistory[];

  if (!blockedModeEntries.length) {
    return sortedDirectEntries;
  }

  const configuredTotal = Number(aggregateEntry.blockedTotalQuestions);
  const fallbackTotal = Number.isFinite(configuredTotal) && configuredTotal > 0 ? configuredTotal : 10;
  const fallbackEntries = sortBlockedSessionEntries(blockedModeEntries.slice(-fallbackTotal));

  if (fallbackEntries.length >= sortedDirectEntries.length) {
    return fallbackEntries;
  }

  return sortedDirectEntries;
}

function closeAnswerReview() {
  showExposureModal.value = false;
  selectedExposureRow.value = null;
  selectedExposure.value = null;
  selectedBlockedSessionEntries.value = [];
}

async function openFullExplanationFromReview() {
  const wordCandidate = activeAnswerReview.value?.word || kc.value?.word || "";
  const word = typeof wordCandidate === "string" ? wordCandidate.trim() : "";
  const persona = selectedFullExplanationPersona.value;

  showFullExplanationModal.value = true;

  if (!word || !persona) {
    fullExplanationContent.value = null;
    return;
  }

  fullExplanationLoading.value = true;
  try {
    const response = await api.post("/v2/lingo-v2/lingo-content/full-explanation", {
      word,
      persona
    });
    const payload = response?.data?.data;
    if (payload && typeof payload === "object") {
      fullExplanationContent.value = {
        spelling: typeof payload.spelling === "string" ? payload.spelling : "",
        vocabulary: typeof payload.vocabulary === "string" ? payload.vocabulary : ""
      };
    } else {
      fullExplanationContent.value = null;
    }
  } catch (error) {
    console.error("Failed to fetch full explanation for dashboard vocabulary review:", error);
    fullExplanationContent.value = null;
  } finally {
    fullExplanationLoading.value = false;
  }
}

function handleFullExplanationContinue() {
  showFullExplanationModal.value = false;
}

watch(showExposureModal, open => {
  if (!open) {
    selectedExposureRow.value = null;
    selectedExposure.value = null;
    selectedBlockedSessionEntries.value = [];
  }
});

// Fetch word by its actual KC word when KC is available
watch(
  kc,
  async (newKc) => {
    const word = newKc?.word;
    if (!word) return;
    isLoading.value = true;
    try {
      await lingoStore.fetchWordByName(decodeURIComponent(String(word)));
    } catch (err) {
      console.error("Failed to fetch word by name:", err);
    } finally {
      isLoading.value = false;
    }
  },
  { immediate: true }
);

const dataLoading = ref(false);
// Tracks whether the last data fetch succeeded, so the template can distinguish
// a transient fetch failure (offer retry) from a genuine missing word.
const dataFetchOk = ref(true);

async function ensureStudentData(id: string) {
  if (!id) return;
  let fetchOk = true;
  // Already hold this student's record (e.g. navigated in from the list) — use it.
  if (!lingoStore.getLingoDataByStudentId(id)) {
    dataLoading.value = true;
    try {
      fetchOk = await lingoStore.fetchStudentLingoData({ studentIds: [id] });
    } finally {
      dataLoading.value = false;
    }
  }
  dataFetchOk.value = fetchOk;
  // Wait for `kc` to recompute against the freshly-loaded data before deciding
  await nextTick();
  reportIfWordMissing(fetchOk);
}

// Retry affordance for the fetch-failed empty state.
function retryLoad() {
  dataFetchOk.value = true;
  ensureStudentData(studentId.value);
}

// Dedupe key for the last reported miss so we don't fire the same alert twice
// (e.g. when both onMounted and the studentId watcher resolve to the same word).
let lastReportedMiss: string | null = null;

// Fire a Slack alert (via the client logger) when a word the user navigated to
// genuinely cannot be resolved after the student's data has finished loading.
function reportIfWordMissing(fetchOk: boolean) {
  if (dataLoading.value) return;          // still loading — not a miss
  if (normalizedKcId.value == null) return; // no word requested
  if (kc.value) return;                    // resolved fine — nothing to report

  const dedupeKey = `${studentId.value}:${kcIdParam.value}`;
  if (lastReportedMiss === dedupeKey) return;
  lastReportedMiss = dedupeKey;

  // On a genuine miss `kc` is null by definition, so the word is unknowable —
  // the failing id maps to no KC in the student's data. wordId + studentId are
  // enough to trace it (and the id fallback chain may itself be the word).
  logger.error(
    WORD_NOT_FOUND_EVENT,
    buildWordNotFoundLog({
      studentId: studentId.value,
      wordId: kcIdParam.value,
      word: kc.value?.word ?? null,
      listType: "vocabulary",
      availableKcCount: searchableKcs.value.length,
      dataFetchOk: fetchOk,
    })
  );
}

onMounted(() => ensureStudentData(studentId.value));

// The router reuses this component across /lingo-vocabulary-word/:studentId/:kcId
// navigations, so onMounted won't re-run. Refetch when the student changes.
watch(studentId, id => ensureStudentData(id));

// kcId-only navigation (word A → B, same student) reuses this component without
// re-running onMounted or the studentId watcher, so the miss-check would never
// re-fire. Re-check after `kc` recomputes against the already-loaded data.
watch(normalizedKcId, async () => {
  await nextTick();
  reportIfWordMissing(dataFetchOk.value);
});

// Level modal state
const showLevelModal = ref(false);
const showLevelIcon = ref(true);
const modalTitle = ref("");
const modalBodyHtml = ref("");
const activeKcForModal = ref<any | null>(null);

function getLevelHtml(level: number | null) {
  switch (Number(level)) {
    case 1:
      return i18n.global.t("lingo_v2.vocabulary.level_1_description").toString();
    case 2:
      return i18n.global.t("lingo_v2.vocabulary.level_2_description").toString();
    case 3:
      return i18n.global.t("lingo_v2.vocabulary.level_3_description").toString();
    case 4:
      return i18n.global.t("lingo_v2.vocabulary.level_4_description").toString();
    case 5:
      return i18n.global.t("lingo_v2.vocabulary.level_5_description").toString();
    case 6:
      return i18n.global.t("lingo_v2.vocabulary.level_6_description").toString();
    case 7:
      return i18n.global.t("lingo_v2.vocabulary.level_7_description").toString();
    default:
      return i18n.global.t("lingo_v2.vocabulary.level_1_description").toString();
  }
}

function openLevelModalFor(kcItem: any) {
  activeKcForModal.value = kcItem || null;
  const lvl = kcItem && kcItem.level ? appendItemLevel(kcItem.level) : null;
  modalTitle.value = lvl ? `Vocabulary Level ${lvl}` : "Vocabulary Level";
  modalBodyHtml.value = getLevelHtml(lvl);
  showLevelIcon.value = true;
  showLevelModal.value = true;
}

function openExposureModal(summaryRow: any, index?: number) {
  selectedExposureRow.value = summaryRow || null;
  const blockedEntries = collectBlockedSessionEntriesFromSummaryRow(summaryRow);
  selectedBlockedSessionEntries.value = blockedEntries;
  if (blockedEntries.length) {
    selectedExposure.value = blockedEntries[0] || null;
  } else {
    selectedExposure.value = summaryRow?.exposure ?? null;
  }
  showExposureModal.value = Boolean(selectedExposureRow.value);
  nextTick(() => {
    console.log("Answers List (Dashboard Vocabulary):", activeAnswerReview.value);
  });
}

const selectedStudent = computed(() => learnerStore.getLearnerById(studentId.value) || null);

function capitalise(str: string) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const displayName = computed(() => {
  const student = selectedStudent.value;
  if (!student) return 'Unknown Student';
  return `${capitalise(student.firstName)} ${capitalise(student.lastName)}`.trim();
});

function appendItemLevel(level) {
  const levelNum = Number(level);
  if (levelNum === 7) {
    return levelNum + "+";
  }
  return level;
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_page_title = _resolveComponent("page-title")!

  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_component_router_link, {
            class: "back-btn",
            to: '/main/lingo-data/student/' + (studentId.value || ''),
            "aria-label": "Back to student lingo data"
          }, {
            default: _withCtx(() => [
              (!isArrowIconString)
                ? (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(ArrowLeftIcon)), {
                    key: 0,
                    class: "back-icon"
                  }))
                : (_openBlock(), _createElementBlock("img", {
                    key: 1,
                    src: _unref(ArrowLeftIcon),
                    alt: "Back",
                    class: "back-icon"
                  }, null, 8, _hoisted_5)),
              _hoisted_6
            ]),
            _: 1
          }, 8, ["to"])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(_component_page_title, {
            pages: pages.value,
            hideFilters: true,
            highlightLast: true
          }, null, 8, ["pages"])
        ]),
        _createElementVNode("h1", _hoisted_8, "Know the word: " + _toDisplayString(kc.value?.word || "-"), 1),
        _createElementVNode("span", _hoisted_9, _toDisplayString(displayName.value), 1)
      ]),
      (dataLoading.value && !kc.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
            _createVNode(SpinnerLoader),
            _hoisted_11
          ]))
        : (kc.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
              _createElementVNode("article", {
                class: _normalizeClass(["summary-card", { 'summary-card--loading': isLoading.value }])
              }, [
                (isLoading.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                      _createVNode(SpinnerLoader),
                      _hoisted_14
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("div", _hoisted_16, [
                    _createElementVNode("p", _hoisted_17, [
                      _createTextVNode(" Know the word: "),
                      _createElementVNode("i", null, _toDisplayString(kc.value.word || "-"), 1)
                    ]),
                    _createElementVNode("div", _hoisted_18, [
                      _createElementVNode("span", {
                        class: "level-text open-modal",
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (openLevelModalFor(kc.value))),
                        style: {"cursor":"pointer"}
                      }, " Level " + _toDisplayString(appendItemLevel(kc.value.level) ?? "-"), 1),
                      _hoisted_19,
                      _createElementVNode("span", _hoisted_20, _toDisplayString(statusBadge.value.label), 1),
                      _createVNode(LingoTooltipIcon, {
                        text: statusDescription.value,
                        placement: "top",
                        size: "sm",
                        ariaLabel: statusBadge.value.label
                      }, null, 8, ["text", "ariaLabel"])
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_21, [
                  _createElementVNode("p", {
                    class: "etymology-text",
                    innerHTML: etymologyText.value
                  }, null, 8, _hoisted_22)
                ]),
                _createElementVNode("div", _hoisted_23, [
                  _createVNode(LingoDataTable, {
                    columns: tableColumns.value,
                    items: exposureSummaryRows.value,
                    "row-key": 'rowKey',
                    "empty-text": 'No exposure recorded yet.',
                    tableClass: "table-borders"
                  }, {
                    "cell-question": _withCtx(({ item, index }) => [
                      _createElementVNode("button", {
                        class: "status-btn",
                        onClick: ($event: any) => (openExposureModal(item, index))
                      }, _toDisplayString(item.question), 9, _hoisted_24)
                    ]),
                    "cell-status": _withCtx(({ item }) => [
                      _createElementVNode("div", _hoisted_25, [
                        _createElementVNode("span", _hoisted_26, _toDisplayString(item.status), 1),
                        (item.status !== '—' && getTooltipTextForExposure(item))
                          ? (_openBlock(), _createBlock(LingoTooltipIcon, {
                              key: 0,
                              text: getTooltipTextForExposure(item),
                              placement: "top",
                              size: "sm",
                              ariaLabel: item.status
                            }, null, 8, ["text", "ariaLabel"]))
                          : _createCommentVNode("", true)
                      ])
                    ]),
                    _: 1
                  }, 8, ["columns", "items", "empty-text"])
                ])
              ], 2)
            ]))
          : (!dataFetchOk.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                _hoisted_28,
                _hoisted_29,
                _createElementVNode("button", {
                  class: "close-modal-btn",
                  onClick: retryLoad
                }, "Retry")
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_30, _hoisted_33))
    ]),
    (selectedExposureRow.value)
      ? (_openBlock(), _createBlock(AnswerReviewModal, {
          key: 0,
          modelValue: showExposureModal.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showExposureModal).value = $event)),
          review: activeAnswerReview.value,
          "teacher-bot": studentLingoData.value?.selectedTeacherBot || null,
          onClose: closeAnswerReview,
          onOpenFullExplanation: openFullExplanationFromReview
        }, null, 8, ["modelValue", "review", "teacher-bot"]))
      : _createCommentVNode("", true),
    _createVNode(BlockedPracticeFullExplanationModal, {
      modelValue: showFullExplanationModal.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showFullExplanationModal).value = $event)),
      loading: fullExplanationLoading.value,
      content: fullExplanationByThread.value,
      "fallback-content": fullExplanationFallbackText.value,
      "teacher-bot": studentLingoData.value?.selectedTeacherBot || null,
      "allow-dismiss": true,
      onContinue: handleFullExplanationContinue
    }, null, 8, ["modelValue", "loading", "content", "fallback-content", "teacher-bot"]),
    _createVNode(LingoModal, {
      modelValue: showLevelModal.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showLevelModal).value = $event)),
      title: modalTitle.value,
      icon: showLevelIcon.value ? _unref(BuildingIcon) : '',
      "show-close-button": true,
      centered: ""
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", { innerHTML: modalBodyHtml.value }, null, 8, _hoisted_34)
      ]),
      footer: _withCtx(() => [
        _createElementVNode("button", {
          class: "close-modal-btn",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (showLevelModal.value = false))
        }, "Close")
      ]),
      _: 1
    }, 8, ["modelValue", "title", "icon"])
  ]))
}
}

})